import { getFormatting } from '../services/formattingCache';
import { groupInteger, formatExactDecimal, trimDecimal } from './exactDecimal';

export const TOKEN_TO_CHAR = { space: ' ', comma: ',', dot: '.', none: '' };

function buildMoneyString(value, decimals) {
  const b = getFormatting();
  const sep = TOKEN_TO_CHAR[b.thousands_separator] ?? ' ';
  const dec = TOKEN_TO_CHAR[b.decimal_separator]   ?? ',';
  const absVal = Math.abs(Number(value));
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(absVal * factor) / factor;
  const parts = rounded.toFixed(decimals).split('.');
  const grouped = groupInteger(parts[0], sep);
  const numStr = decimals > 0 ? grouped + dec + parts[1] : grouped;
  return withCurrency(numStr, Number(value) < 0);
}

// Currency label + placement + negative style (Settings ▸ Formatting) around an unsigned number string.
function withCurrency(numStr, negative) {
  const b = getFormatting();
  const label = b.currency_label ?? 'FCFA';
  const formatted = b.placement === 'prefix' ? label + numStr : numStr + ' ' + label;
  if (!negative) return formatted;
  return b.negative_style === 'parentheses' ? `(${formatted})` : `-${formatted}`;
}

export function formatMoney(value) {
  if (value == null || isNaN(Number(value))) return '—';
  return buildMoneyString(value, getFormatting().amount_decimals ?? 0);
}

export function formatMoneyPrecise(value) {
  if (value == null || isNaN(Number(value))) return '—';
  return buildMoneyString(value, getFormatting().rate_decimals ?? 2);
}

export function formatNumber(value, decimals = 0) {
  if (value == null || isNaN(Number(value))) return '—';
  const b = getFormatting();
  const sep = TOKEN_TO_CHAR[b.thousands_separator] ?? ' ';
  const dec = TOKEN_TO_CHAR[b.decimal_separator]   ?? ',';
  const num = Number(value);
  const absVal = Math.abs(num);
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(absVal * factor) / factor;
  const parts = rounded.toFixed(decimals).split('.');
  const grouped = groupInteger(parts[0], sep);
  const numStr = decimals > 0 ? grouped + dec + parts[1] : grouped;
  return num < 0 ? '-' + numStr : numStr;
}

// Exact numeric strings from the API (col::text): trimmed, app separators, no float conversion.
export function formatExact(value, minFrac = 0) {
  const b = getFormatting();
  return formatExactDecimal(value, TOKEN_TO_CHAR[b.thousands_separator] ?? ' ', TOKEN_TO_CHAR[b.decimal_separator] ?? ',', minFrac);
}

// GES-406 — exact API money text (never rounded; padded to the configured amount decimals) with the currency label.
export function formatMoneyExact(value) {
  const text = trimDecimal(value);
  if (text == null || !/^-?\d+(\.\d+)?$/.test(text)) return '—';
  return withCurrency(formatExact(text.replace(/^-/, ''), getFormatting().amount_decimals ?? 0), text.startsWith('-'));
}

// GES-406 — a plain quantity with up to `maxDecimals` (trailing zeros dropped): the old toLocaleString
// default, on the configured separators instead of a hard-coded locale.
export function formatQty(value, maxDecimals = 3) {
  if (value == null || value === '' || isNaN(Number(value))) return '—';
  const f = 10 ** maxDecimals;
  return formatExact(String(Math.round(Number(value) * f) / f));
}

function dateForFormat(value) {
  if (!value) return null;
  let d;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, day] = value.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(value);
  }
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(value) {
  const d = dateForFormat(value);
  if (!d) return '—';
  const b = getFormatting();
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  switch (b.date_format) {
    case 'mm/dd/yyyy': return `${mm}/${dd}/${yyyy}`;
    case 'yyyy-mm-dd': return `${yyyy}-${mm}-${dd}`;
    default:           return `${dd}/${mm}/${yyyy}`;
  }
}

// Render-only variants use the same configured date order as formatDate. Keep ISO
// slicing and date-input values out of these helpers: those are data keys, not UI.
export function formatDateTime(value) {
  const d = dateForFormat(value);
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(value)} ${hh}:${min}`;
}

export function formatShortDate(value) {
  const d = dateForFormat(value);
  if (!d) return '—';
  const b = getFormatting();
  const day = String(d.getDate()).padStart(2, '0');
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return b.date_format === 'yyyy-mm-dd' ? `${d.getFullYear()} ${month} ${day}`
    : b.date_format === 'mm/dd/yyyy' ? `${month} ${day}`
      : `${day} ${month}`;
}

export function formatMonthYear(value) {
  const d = dateForFormat(value);
  if (!d) return '—';
  const b = getFormatting();
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return b.date_format === 'yyyy-mm-dd' ? `${d.getFullYear()} ${month}` : `${month} ${d.getFullYear()}`;
}

export function parseNumberFromInput(str) {
  if (!str || typeof str !== 'string') return null;
  const b = getFormatting();
  const sep = TOKEN_TO_CHAR[b.thousands_separator] ?? ' ';
  const dec = TOKEN_TO_CHAR[b.decimal_separator]   ?? ',';
  let cleaned = str;
  if (sep) cleaned = cleaned.split(sep).join('');
  if (dec && dec !== '.') cleaned = cleaned.replace(dec, '.');
  const result = parseFloat(cleaned);
  return isNaN(result) ? null : result;
}

// 43015 — one 'updated <time>' format for every Refresh control (button + benchmarks freshness).
export const formatRefreshedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(+d) ? null : `${formatShortDate(d)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
